import { api } from "./api";
import { Subject } from "./subjects";
import { normalizeForSearch, attachDropdownKeyboardNav, clampDropdownToViewport, trackUnsavedInput, navigateSmoothly } from "./generics";
import { renderPostHocNotifyPrompt } from "./notifyEmail";

// Odpowiednik addLecturer.ts, dla dodawania nowych przedmiotów do katalogu (moduł
// "Przedmioty") - dużo prostszy: wydział jest ZAWSZE znany z kontekstu strony (nie ma
// odpowiednika technicznego koszyka "Bez przypisanego wydziału"/pickera wydziału), więc nie ma
// tu wieloetapowego rozstrzygania - klik na dopasowanie albo "+ Dodaj przedmiot" prowadzi wprost
// na stronę przedmiotu (istniejącego albo świeżo utworzonego), gdzie jest już pełny formularz
// opinii (patrz subject_detail.ts) - w odróżnieniu od addLecturer.ts nie ma tu połączonego w
// jeden dialog kroku "dodaj pierwszą opinię od razu".
let openDialog: HTMLDialogElement | null = null;

export function renderAddSubjectButton(
    container: HTMLElement,
    facultyId: number,
    universitySlug: string,
    facultySlug: string,
    getPrefillQuery: () => string,
    small = false,
    emphasized = false,
    queryLabel?: string,
    large = false,
): void {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = small ? "add-comment-btn" : "add-person-btn";
    if (small && emphasized) btn.classList.add("add-comment-btn-emphasized");
    if (small && large) btn.classList.add("add-comment-btn-large");
    const label = small && queryLabel
        ? `Dodaj „${queryLabel}”`
        : small ? "Dodaj" : "Dodaj przedmiot";
    const icon = document.createElement("span");
    icon.className = "add-comment-btn-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "+";
    btn.append(icon, ` ${label}`);
    btn.addEventListener("click", () => {
        openAddSubjectDialog(facultyId, universitySlug, facultySlug, getPrefillQuery());
    });
    container.appendChild(btn);
}

function openAddSubjectDialog(
    facultyId: number, universitySlug: string, facultySlug: string, prefillQuery: string,
): void {
    openDialog?.close();

    const dialog = document.createElement("dialog");
    dialog.className = "add-review-dialog add-person-dialog";

    const title = document.createElement("h2");
    title.className = "add-review-dialog-title";
    title.textContent = "Dodaj przedmiot";

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "add-review-dialog-close";
    closeBtn.setAttribute("aria-label", "Zamknij");
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", () => dialog.close());

    const formContainer = document.createElement("div");
    formContainer.className = "comment-form-box";

    dialog.append(title, closeBtn, formContainer);
    document.body.appendChild(dialog);

    dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener("close", () => {
        if (openDialog === dialog) openDialog = null;
        dialog.remove();
    });
    openDialog = dialog;

    dialog.showModal();

    renderAddSubjectForm(formContainer, facultyId, universitySlug, facultySlug, prefillQuery);
}

async function renderAddSubjectForm(
    container: HTMLElement, facultyId: number, universitySlug: string, facultySlug: string, prefillQuery: string,
): Promise<void> {
    const loading = document.createElement("p");
    loading.textContent = "Wczytywanie...";
    container.appendChild(loading);

    const catalogResult = await api.getSubjectsCatalog(facultyId);
    container.replaceChildren();

    const errorEl = document.createElement("span");
    errorEl.className = "error";

    const existingSubjects: Subject[] = catalogResult.ok ? catalogResult.data.subjects : [];
    if (!catalogResult.ok) errorEl.textContent = `Błąd wczytywania przedmiotów: ${catalogResult.error}`;

    const nameField = document.createElement("label");
    nameField.className = "lecturer-field";
    const nameLabel = document.createElement("span");
    nameLabel.className = "field-label-text";
    nameLabel.textContent = "Nazwa przedmiotu:";

    const searchWrapper = document.createElement("div");
    searchWrapper.className = "search-with-suggestions";

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.required = true;
    nameInput.maxLength = 200;
    nameInput.autocomplete = "off";
    nameInput.value = prefillQuery;
    trackUnsavedInput(nameInput);

    const dropdown = document.createElement("div");
    dropdown.className = "search-suggestions";
    searchWrapper.append(nameInput, dropdown);
    nameField.append(nameLabel, searchWrapper);

    const resolveError = document.createElement("span");
    resolveError.className = "error";

    const pendingNotice = document.createElement("div");
    pendingNotice.className = "add-person-thanks-banner";
    pendingNotice.hidden = true;

    container.append(nameField, errorEl, resolveError, pendingNotice);

    function closeDropdown(): void {
        dropdown.classList.remove("open");
    }

    function goToSubject(subject: Pick<Subject, "slug">): void {
        navigateSmoothly(`/${universitySlug}/${facultySlug}/przedmioty/${subject.slug}/`);
    }

    function renderSuggestions(query: string): void {
        dropdown.replaceChildren();
        const normalizedQuery = normalizeForSearch(query);
        if (!normalizedQuery) { closeDropdown(); return; }

        const matches = existingSubjects.filter(s => normalizeForSearch(s.name).includes(normalizedQuery));
        const trimmedQuery = query.trim();
        const exactMatch = existingSubjects.some(s => normalizeForSearch(s.name) === normalizedQuery);

        if (trimmedQuery && !exactMatch) {
            const createItem = document.createElement("button");
            createItem.type = "button";
            createItem.className = "search-suggestion-item search-suggestion-create";
            createItem.textContent = `+ Dodaj przedmiot: "${trimmedQuery}"`;
            createItem.addEventListener("click", async () => {
                createItem.disabled = true;
                createItem.textContent = "Dodawanie...";
                nameInput.disabled = true;
                const res = await api.createSubjectCatalogEntry(trimmedQuery, facultyId);
                if (!res.ok) {
                    nameInput.disabled = false;
                    resolveError.textContent = `Błąd: ${res.error}`;
                    createItem.disabled = false;
                    createItem.textContent = `+ Dodaj przedmiot: "${trimmedQuery}"`;
                    return;
                }
                // "active" (AI zatwierdziła nazwę) - strona przedmiotu już istnieje publicznie
                // (patrz SubjectDetailView.queryset - pokazuje tylko status=ACTIVE), można od
                // razu tam przejść. W przeciwnym razie (AI odrzuciła/nie odpowiedziała - patrz
                // check_subject_name_with_ai w backendzie) strona zwróciłaby 404, dopóki admin
                // ręcznie nie zatwierdzi - zamiast nawigować, zostajemy w dialogu z komunikatem
                // (ten sam wzorzec co "Nowo dodana osoba czeka na moderację" w addLecturer.ts).
                if (res.data.subject.status === "active") {
                    goToSubject(res.data.subject);
                    return;
                }
                closeDropdown();
                nameField.hidden = true;
                errorEl.textContent = "";
                resolveError.textContent = "";
                pendingNotice.hidden = false;
                const text = document.createElement("p");
                text.textContent = `Dziękujemy! Przedmiot „${trimmedQuery}” został zapisany i będzie widoczny po sprawdzeniu przez moderację.`;
                pendingNotice.replaceChildren(
                    text,
                    renderPostHocNotifyPrompt([{ model: "subject", pk: res.data.subject.id }]),
                );
            });
            dropdown.appendChild(createItem);
        }

        if (matches.length > 0) {
            const header = document.createElement("div");
            header.className = "search-suggestions-header";
            header.textContent = "Czy chodziło Ci o:";
            dropdown.appendChild(header);
        }
        matches.forEach(subject => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.append(subject.name);
            if (subject.number_of_comments > 0) {
                const hint = document.createElement("span");
                hint.className = "search-suggestion-hint";
                hint.textContent = ` (Opinie: ${subject.number_of_comments})`;
                item.appendChild(hint);
            }
            item.addEventListener("click", () => goToSubject(subject));
            dropdown.appendChild(item);
        });

        if (dropdown.children.length > 0) {
            clampDropdownToViewport(nameInput, dropdown);
            dropdown.classList.add("open");
        } else {
            closeDropdown();
        }
    }

    nameInput.addEventListener("input", () => renderSuggestions(nameInput.value));
    nameInput.addEventListener("focus", () => renderSuggestions(nameInput.value));
    document.addEventListener("click", (event) => {
        if (!searchWrapper.contains(event.target as Node)) closeDropdown();
    });
    attachDropdownKeyboardNav(nameInput, dropdown);

    if (prefillQuery.trim()) renderSuggestions(prefillQuery);
}
